import React from 'react';
import {
  BarChart,
  Bar,
  Rectangle,
  LineChart,
  Line,
  XAxis,
  YAxis,
  AreaChart,
  Area,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
} from 'recharts';
import spinner from '../../../images/spinner-ripple.svg';
import useSettings from "../../../hooks/Settings";
import AngledAxisTick from '../AngledAxisTick';
import AnalyticsEmptyState from "../../dashboard/AnalyticsEmptyState";
import { CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH } from '../../../helpers/chartPalette';
import { getSeriesColor } from '../../../helpers/chartPalette';
import useChartColors from '../../../hooks/useChartColors';
import OutputFieldsTooltipSection from './OutputFieldsTooltipSection';
import ChartDataTable from '../../graphData/ChartDataTable';
import { seriesTable } from '../../../helpers/dataHelpers/chartDataTable';

// Custom tooltip component that displays the date, total, and project breakdown with aggregated subproject totals.
const CustomTooltip = ({ active, payload, label, portfolio, projectHierarchy, fieldSummaries }) => {
  if (!active || !payload?.length) return null;
  const rowData = payload[0].payload;

  return (
    <div style={{ backgroundColor: 'white', border: '1px solid #ccc', padding: 10, borderRadius: 4 }}>
      <h2 style={{ margin: 0, fontWeight: 'bold' }}>{label}</h2>
      {Object.keys(rowData).map(key => {
        if (key === 'name') return null;
        const { total, projects } = rowData[key] || {};
        // Cards without a per-project breakdown (e.g. the Trajecten card) only
        // provide a total — still show it; just skip the breakdown below.
        if (total === undefined) return null;

        let sortedPaths = [];
        let nodeMap = {};
        if (projects) {
          // Build entries with depth and path
          const entries = Object.entries(projects).map(([projName, val]) => ({
            projName,
            val,
            depth: projectHierarchy[projName]?.depth || 0,
            path: projectHierarchy[projName]?.path || ''
          }));

          // Map paths to nodes and attach children
          entries.forEach(e => { nodeMap[e.path] = { ...e, children: [] }; });
          entries.forEach(e => {
            const parts = e.path.split('.').map(Number);
            if (parts.length > 1) {
              const parentPath = parts.slice(0, -1).join('.');
              const parent = nodeMap[parentPath];
              if (parent) parent.children.push(nodeMap[e.path]);
            }
          });

          // Recursively compute aggregated totals
          const computeTotals = node => {
            let sum = node.val;
            node.children.forEach(child => { sum += computeTotals(child); });
            node.total = sum;
            return sum;
          };
          // Seed from every node whose parent isn't present (forest roots).
          // Depth alone isn't reliable: when the portfolio org has no own data,
          // its child projects are the shallowest nodes shown — filtering on
          // depth === 0 would then leave every node's total uncomputed.
          Object.values(nodeMap).forEach(node => {
            const parts = node.path.split('.');
            const parentPath = parts.length > 1 ? parts.slice(0, -1).join('.') : null;
            if (!parentPath || !nodeMap[parentPath]) computeTotals(node);
          });

          // Sort nodes by path (treating missing segments as -1 so parents come first)
          sortedPaths = Object.keys(nodeMap).sort((a, b) => {
            const pa = a.split('.').map(Number);
            const pb = b.split('.').map(Number);
            const len = Math.max(pa.length, pb.length);
            for (let i = 0; i < len; i++) {
              const na = pa[i] !== undefined ? pa[i] : -1;
              const nb = pb[i] !== undefined ? pb[i] : -1;
              if (na !== nb) return na - nb;
            }
            return 0;
          });
        }

        return (
          <div key={key} style={{ marginTop: 5 }}>
            <div><strong>{key}</strong>: {total}</div>
            {projects && portfolio !== 'none' && sortedPaths.map(path => {
              const node = nodeMap[path];
              return (
                <div
                  key={path}
                  style={{
                    fontSize: 12,
                    marginLeft: node.depth * 20 + 'px',
                    marginTop: 2,
                  }}
                >
                  {node.projName}: {node.total}
                </div>
              );
            })}
          </div>
        );
      })}
      <OutputFieldsTooltipSection fieldSummaries={fieldSummaries} label={label} />
    </div>
  );
};

const CustomLegend = ({ payload, growth, graphColor }) => {
  if (!payload) return null;
  return (
    <ul
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        flexWrap: 'wrap',
      }}
    >
      {payload.map((entry, index) => {
        const title = entry.value || '';
        const growthValue =
          growth && growth[title] !== undefined ? growth[title] : 0;
        return (
          <li
            key={`legend-item-${index}`} style={{ marginRight: 10, color: graphColor || 'black' }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                backgroundColor: entry.color,
                marginRight: 5,
              }}
            ></span>
            {title}
            {/* {averageGrowth === 'true' && (
              <span>
                : {growthValue >= 0 ? '+' : ''}
                {growthValue.toFixed(1)}%
              </span>
            )} */}
          </li>
        );
      })}
    </ul>
  );
};

/**
 * Line/area or bar chart of one or more outputs over time.
 *
 * @param {object} props
 * @param {Array<object>} props.data - Rows `{ name: '12-5-2026', [outputTitle]: { total, projects } }`
 * @param {boolean} props.isLoading - Shows the spinner instead of the chart
 * @param {Array<{ID: string, Title: string}>} [props.outputs] - One series per output
 * @param {'line'|'bar'|string} [props.graphType] - 'bar' renders bars, anything else an area
 * @param {object} [props.growth] - Growth per output (legend, currently unused)
 * @param {number|string} [props.height] - Chart height
 * @param {number|string} [props.width] - Chart width
 * @param {string} [props.portfolio] - 'none' hides the per-project breakdown in the tooltip
 * @param {string|object} [props.color] - Series colour(s)
 * @param {string} [props.graphColor] - Axis and tick colour
 * @param {object} [props.projectHierarchy] - `{ [projectName]: { depth, path } }` for the tooltip breakdown
 * @param {boolean} [props.displayLegend] - Legend toggle (legend currently commented out)
 * @param {(row: object) => void} [props.onBarClick] - Click handler for a bar
 * @param {(row: object, labelProps: object) => JSX.Element} [props.barTopLabel] - Label above each bar
 * @param {import('./OutputFieldsTooltipSection').OutputFieldSummaries} [props.fieldSummaries] - The
 *   activity's other fields per date, shown under the total in the tooltip
 * @param {string} [props.tableCategoryLabel='Datum'] - Kop van de eerste kolom van de cijfertabel (PDF, kopie)
 * @param {string} [props.tableValueLabel] - Kop van de waardekolom als er één reeks zonder leesbare titel is ('value')
 */
const OutputMultiLineGraph = ({
  data,
  isLoading,
  outputs,
  graphType,
  growth,
  height,
  width,
  portfolio,
  color,
  graphColor,
  projectHierarchy,
  displayLegend,
  onBarClick,
  barTopLabel,
  fieldSummaries,
  tableCategoryLabel = 'Datum',
  tableValueLabel,
}) => {
  const latestData = data && data.length ? data[data.length - 1] : {};

  // Hooks
  const { inLiveReport } = useChartColors()
  const secundairyColor = useSettings().SecundairyColor;

  // Callers pass `color` in one of two shapes:
  //   string  — single hex used for every output (dashboard static/dynamic cards)
  //   object  — map of output.ID → hex (live-report builder, per-output colors)
  // Normalising here so both bar and area paths resolve colour the same way.
  // Buiten een live rapportage komt de kleur uit het vaste palet, op index —
  // anders krijgen alle outputs in dezelfde grafiek dezelfde kleur, want de
  // dashboardkaart geeft er één string voor allemaal mee.
  const resolveOutputColor = (output, index = 0) => {
    if (!inLiveReport) return getSeriesColor(index);
    if (typeof color === 'string' && color) return color;
    if (color && output?.ID && color[output.ID]) return color[output.ID];
    return secundairyColor;
  };

  return (
    <>
      {/* {averageGrowth === 'true' && (
        <h3 style={{ color: graphColor || 'black' }}>
          Gemiddelde groei:{' '}
          {growth?.average && growth?.average > 0 ? '+' : ''}
          {`${growth?.average?.toFixed(1)}%`}
        </h3>
      )} */}
      {isLoading ? (
        <div id="graph-loading-container">
          <img src={spinner} alt="spinner" className="spinner" />
        </div>
      ) : (
        <div className='graph-wrapper'>
         {data.length > 0 ? (
          <ResponsiveContainer
            width={width || '100%'}
            height={height || 400}
            className='graph-responsive-container'
          >
          {graphType === 'bar' ? (
            <BarChart
              data={data}
              margin={{ top: barTopLabel ? 30 : 5, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis
                dataKey="name"
                stroke={graphColor || 'black'}
                tick={<AngledAxisTick fill={graphColor || 'black'} />}
                interval={0}
                height={95}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke={graphColor || 'black'}
                tick={{ fill: graphColor || 'black' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    portfolio={portfolio}
                    projectHierarchy={projectHierarchy}
                    fieldSummaries={fieldSummaries}
                  />
                }
              />
              {/* {displayLegend !== false &&
                <Legend
                  content={
                    <CustomLegend
                      growth={growth?.perOutput}
                      latestData={latestData}
                      // averageGrowth={averageGrowth}
                      graphColor={graphColor}
                    />
                  }
                />
              } */}
              {outputs &&
                outputs.map((output, index) => {
                  const outputColor = resolveOutputColor(output, index);
                  return (
                  <Bar
                    key={`bar-${index}`}
                    name={output.Title}
                    dataKey={(row) =>
                      row[output.Title] ? row[output.Title].total : 0
                    }
                    fill={outputColor}
                    stroke={CHART_MARK_STROKE}
                    strokeWidth={CHART_MARK_STROKE_WIDTH}
                    activeBar={
                      <Rectangle fill={outputColor} stroke={outputColor} />
                    }
                    onClick={onBarClick ? (entry) => onBarClick(entry?.payload ?? entry) : undefined}
                    style={onBarClick ? { cursor: 'pointer' } : undefined}
                  >
                    {barTopLabel && (
                      <LabelList
                        dataKey={(row) =>
                          row[output.Title] ? row[output.Title].total : 0
                        }
                        position="top"
                        content={(labelProps) => {
                          const { index } = labelProps
                          const payload = data?.[index]
                          return barTopLabel(payload, labelProps)
                        }}
                      />
                    )}
                  </Bar>
                  );
                })}
            </BarChart>
          ) : (
            <AreaChart
              data={data}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis
                dataKey="name"
                stroke={graphColor || 'black'}
                tick={<AngledAxisTick fill={graphColor || 'black'} />}
                height={70}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke={graphColor || 'black'}
                tick={{ fill: graphColor || 'black' }}
                axisLine={false}
                tickLine={false} 
              />
              <Tooltip
                content={
                  <CustomTooltip
                    portfolio={portfolio}
                    projectHierarchy={projectHierarchy}
                    fieldSummaries={fieldSummaries}
                  />
                }
              />
               {/* {displayLegend !== false && 
                <Legend
                  content={
                    <CustomLegend
                      growth={growth?.perOutput}
                      latestData={latestData}
                      // averageGrowth={averageGrowth}
                      graphColor={graphColor}
                    />
                  }
                />
              } */}
              {outputs &&
                outputs.map((output, index) => {
                  const outputColor = resolveOutputColor(output, index);
                  return (
                  <React.Fragment key={`line-${index}`}>
                    <Area
                      type="monotone"
                      name={output.Title}
                      dataKey={(row) =>
                        row[output.Title] ? row[output.Title].total : 0
                      }

                      stroke={outputColor}
                      fill={outputColor}
                      fillOpacity={0.1}
                      strokeWidth="2"
                    />
                  </React.Fragment>
                  );
                })}
            </AreaChart>
          )}
        </ResponsiveContainer>
          ) : (

            // No data available
            <AnalyticsEmptyState />
          )}
        </div>
      )}
      {/* Buiten .graph-wrapper: dat vak zet zijn inhoud naast elkaar
          (display: flex), en dan schoof de tabel over de staven. */}
      {!isLoading && data.length > 0 && (
        <ChartDataTable
          table={seriesTable(
            data,
            (outputs || []).map(output => ({
              key: output.Title,
              label: tableValueLabel && outputs.length === 1 ? tableValueLabel : output.Title,
            })),
            tableCategoryLabel,
          )}
        />
      )}
    </>
  );
};

export default OutputMultiLineGraph;



